import { ReactNode } from "react";
import { TeamCalendar } from "./TeamCalendar";
import { SprintBoard } from "./SprintBoard";
import { SprintBurndown } from "./SprintBurndown";
import { TeamChat } from "./TeamChat";
import { TeamSplittingScenario } from "./TeamSplittingScenario";

export interface Scenario {
    id: string;
    title: string;
    description: string;
    component: ReactNode;
    initialAiMessage: string;
    idealAnswer: string;
    context: string;
}

export const scenarios: Scenario[] = [
    {
        id: "calendar",
        title: "Meeting Calendar",
        description: "Analyze the team's current meeting schedule.",
        component: <TeamCalendar />,
        initialAiMessage: "You take over a new team. They show you their current team meeting calendar. What is your reaction?",
        idealAnswer: "This calendar might differ massively from some guide like scrum but that shall not be the goal of the way of working of a team to fulfill some guidelines. So first, I ask why the team came up with the solutions to their circumstances. There might be some decisions based in lesser knowledge and there I can coach them to make better decisions. But there might also be decisions based on circumstances that are not visible to me yet. If that combination of circumstances and reactions to it creates friction and waste, I must also consider changing the circumstances. Example: The team might have opted for 3 Planning Meetings because of time zones, changing managerial inputs or dependencies to other teams that need to be resolved in between meetings. As a result, I as the agile coach should understand the impact those circumstances have and if bad, work on them before signaling to the team that THEY did something wrong.",
        context: "The user is shown a Team Meeting Calendar with 3 Planning Meetings, 1 Retrospective and 2 Daily Standups. This does not follow the Scrum Guide could be a valid way of working if the circumstances justify it."
    },
    {
        id: "standup",
        title: "Standup Video",
        description: "Observe the team's daily standup dynamics.",
        component: (
            <div className="space-y-2">
                <div className="aspect-video rounded-xl overflow-hidden shadow-sm border border-border">
                    <iframe
                        width="100%"
                        height="100%"
                        src="https://www.youtube.com/embed/kKIc1NFO-AU?si=FWRr1XalrHiNPG1L&start=26"
                        title="Daily Standup Meeting"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    ></iframe>
                </div>
                <div className="text-xs text-muted-foreground text-right">
                    <a href="https://www.youtube.com/watch?v=kKIc1NFO-AU&t=26s" target="_blank" rel="noreferrer" className="underline hover:text-primary">
                        Watch on YouTube
                    </a>
                </div>
            </div>
        ),
        initialAiMessage: "You've just watched the team's daily standup. What observations did you make regarding the team dynamics and the ceremony format?",
        idealAnswer: "Team Coach Focus: Who participates? Body language? Conflicts? Energy levels? Method Champion Focus: 3 questions format? Time-boxed? Is it coordination or just a status report? Identifies lack of engagement or clarity.",
        context: "The user watches a video of a potentially dysfuntional daily standup."
    },
    {
        id: "sprintboard",
        title: "Sprint Board",
        description: "Review the current work in progress.",
        component: <SprintBoard />,
        initialAiMessage: "This is the current sprint board with 6 developers. The PO says slowing down isn't an option, despite inefficiencies. How do you respond?",
        idealAnswer: "Method Champion: Understand WIP and flow. More bodies != faster flow. Limit WIP. Knowledge Enabler: Different skillsets are a feature; suggest pair programming. Protector: Reframe the conversation. Speed without capability causes burnout. Sustainable pace.",
        context: "User sees a Kanban board with 6 dev tickets in progress, assigned to different people."
    },
    {
        id: "burndown",
        title: "Sprint Burndown",
        description: "Analyze the previous sprint's performance.",
        component: <SprintBurndown />,
        initialAiMessage: "Looking at this burndown chart from the last sprint, what patterns do you see? How would you address this with the team?",
        idealAnswer: "Method Champion: Scope changes? Estimation issues? Unplanned work? Knowledge Enabler: Knowledge gaps? Unexpected complexity? Strong Response: Asks clarifying questions BEFORE proposing solutions. Looks for patterns.",
        context: "User sees a burndown chart that starts high, plateaus, and fails to drop to zero."
    },
    {
        id: "github",
        title: "GitHub Notification",
        description: "Handle a request from a stakeholder.",
        component: (
            <div className="rounded-xl overflow-hidden border shadow-sm">
                <img
                    src="/screenshots/Github.png"
                    alt="Screenshot of a stakeholder notification or request"
                    className="w-full h-auto"
                />
            </div>
        ),
        initialAiMessage: "You've received this notification in GitHub. How do you handle this request?",
        idealAnswer: "Servant Leader: Understand 'why', prioritize in backlog. Don't just say yes/no. Conflict Navigator: Facilitate conversation. There seems to be urgency. Align expectations without disrupting current goals.",
        context: "User sees a GitHub notification/issue from a stakeholder requesting immediate changes."
    },
    {
        id: "boss",
        title: "Boss Email",
        description: "Resonse to a direct mandate from leadership.",
        component: (
            <div className="border rounded-xl bg-card shadow-sm overflow-hidden font-sans text-sm">
                {/* Email Header */}
                <div className="bg-muted/30 p-4 border-b space-y-2">
                    <div className="flex justify-between items-start">
                        <div className="space-y-0.5">
                            <div className="flex items-baseline gap-2">
                                <span className="font-bold text-foreground">From:</span>
                                <span className="text-foreground">Frieder (CEO) &lt;frieder@company.com&gt;</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="font-bold text-muted-foreground">To:</span>
                                <span className="text-muted-foreground">You &lt;team-lead@company.com&gt;</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="font-bold text-muted-foreground">Subject:</span>
                                <span className="font-semibold text-foreground">Great idea from conference!</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="font-bold text-muted-foreground">Date:</span>
                                <span className="text-muted-foreground">Today, 10:42 AM</span>
                            </div>
                        </div>
                        <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold border-2 border-white shadow-sm">
                            FR
                        </div>
                    </div>
                </div>

                {/* Email Body */}
                <div className="p-6 space-y-4 text-zinc-200 leading-relaxed bg-zinc-950">
                    <p>Hi,</p>
                    <p>
                        I am on this huge conference and I saw this awesome keynote speech. Now I know what your team needs.
                        Please introduce lean methods asap.
                    </p>
                    <p>
                        Will be back in office in 8 days, let’s hope we can see an impact then already!
                    </p>
                    <p>
                        I’m going offline again, CU.
                    </p>
                    <p>
                        Best regards<br />
                        Frieder
                    </p>
                </div>
            </div>
        ),
        initialAiMessage: "Frieder (CEO) just sent this email. He wants 'Lean Methods' introduced immediately. What is your reply?",
        idealAnswer: "Protector: Refuse rushed mandate but engage thoughtfully. Helicopter: What is the real problem? (Quality? Cost?). Method Champion: Lean takes months/years, not days. Requires buy-in, not mandates.",
        context: "User reads an email from the CEO demanding immediate implementation of 'Lean Methods' within 8 days."
    },
    {
        id: "chat",
        title: "Team Chat Incident",
        description: "Manage a conflict in the public channel.",
        component: <TeamChat />,
        initialAiMessage: "This conversation just happened in the public team channel. How do you react to Kim's comments?",
        idealAnswer: "Disciplinizer: Address disrespect directly but conversationally. Focus on values. Team Coach: 1:1 with Susanne (check-in) and Kim (understand reaction). Address psychological safety. Moderator: Build bridges. It's not about right/wrong, but respect.",
        context: "User sees a chat log where 'Kim' shames 'Susanne' for liking meetings/not coding."
    },
    {
        id: "split",
        title: "Team Split Proposal",
        description: "Advise on organizational structure.",
        component: <TeamSplittingScenario />,
        initialAiMessage: "The PO suggests splitting the team to reduce 'communication overhead' and offers these 3 options. Which one do you recommend, or do you have an alternative?",
        idealAnswer: "Best Response: Don't default to splitting immediately. Diagnose the real problem (Overload vs Coordination friction). Helicopter: Is overhead caused by size or poor structure? Knowledge Enabler: splitting risks silos. Networker: How will dependencies be managed?",
        context: "Visual showing a 12-person team and 3 split options: By Customer, By Tech, By Location."
    }
];
